import type { PromoCodesPageStore } from '@ctct/state-client-layer';

import { PeriodBasedPromoCodeService } from './services/PeriodBasedPromoCodeService';
import { PromoCodeApplicationService } from './services/PromoCodeApplicationService';
import { PromoCodesService } from './services/PromoCodesService';
import { PromoCodesVisibilityService } from './services/PromoCodesVisibilityService';
import { TranslationService } from './services/TranslationService';
import { ValidationService } from './services/ValidationService';
import type { Init, PromoCodesUrlAdapter } from './types';
import type { MergedDataReturnType, MergedHandlersReturnType } from '../../types';
import type { WidgetLifecycle } from '../WidgetLifecycle';
import { WithApi } from '../WithApi';

export type PromoCodesData = MergedDataReturnType<[PromoCodesService]>;

export type PromoCodesHandlers = MergedHandlersReturnType<[PromoCodesService]>;

type TApi = ReturnType<PromoCodesService['api']> &
  ReturnType<ValidationService['api']> &
  ReturnType<TranslationService['api']> &
  ReturnType<PromoCodeApplicationService['api']> &
  ReturnType<PeriodBasedPromoCodeService['api']>;

export class PromoCodesManager extends WithApi<TApi> {
  private promoCodesPageStore: PromoCodesPageStore | null = null;

  constructor(private urlAdapter: PromoCodesUrlAdapter | null = null) {
    super();
  }

  /**
   * Queues promo codes for re-validation after a plan change and resets validation state.
   * Valid codes are re-applied once the selected plan and rating payload match `planId`.
   */
  public async preparePromoRevalidationAfterPlanChangeWhenReady(
    planId: number,
    payloadPromoCodes: string[],
    inboundPromoCodes: string[] = []
  ): Promise<void> {
    await this.getApiWhenReady();

    this.preparePromoRevalidationAfterPlanChange(planId, payloadPromoCodes, inboundPromoCodes, this.getUrlPromoCodes());
  }

  private preparePromoRevalidationAfterPlanChange(
    planId: number,
    payloadPromoCodes: string[],
    inboundPromoCodes: string[],
    urlPromoCodes: string[]
  ) {
    if (!this.promoCodesPageStore) {
      return;
    }

    const normalizedCodes = [
      ...new Set([...payloadPromoCodes, ...urlPromoCodes, ...inboundPromoCodes].map((code) => code.toUpperCase())),
    ];

    this.promoCodesPageStore.setState({
      promoCodeFormErrors: [],
      failedUrlParamCodes: [],
      promoCodesToBeValidated: normalizedCodes,
      promoCodeTextFieldValue: '',
      promoRevalidationPlanId: planId,
    });
  }

  public getUrlPromoCodes(): string[] {
    return this.urlAdapter?.getPromoCodesParams().promoCodes ?? [];
  }

  public promoCodesInit = ({
    pageStore,
    t,
    queries,
    externalStateValues,
    billingStrategy,
    shouldHidePromoCodes,
    isHidePromoCodesFeatureGateEnabled,
  }: Init) => {
    const [promoCodeValidation, ratingQuery, planDetailQuery, customerQuery] = queries;

    const { selectedEmailPlanId, paymentPeriod, ratingRequest } = externalStateValues;
    const { urlAdapter } = this;

    this.promoCodesPageStore = pageStore;

    const promoCodesService = new PromoCodesService(
      pageStore,
      promoCodeValidation,
      this.urlAdapter,
      ratingRequest,
      ratingQuery.data,
      planDetailQuery.data,
      selectedEmailPlanId,
      // null while pending so URL promos do not collapse to in-house and hit BaaS early
      billingStrategy.data,
      t,
      isHidePromoCodesFeatureGateEnabled,
      shouldHidePromoCodes,
      billingStrategy.isPending,
      ratingQuery.isPending
    );
    const visibility = new PromoCodesVisibilityService(shouldHidePromoCodes);
    const validation = new ValidationService(pageStore, t, promoCodesService);
    const translations = new TranslationService(t);
    const periodBasedPromoCodes = new PeriodBasedPromoCodeService(pageStore, promoCodesService, t, paymentPeriod);
    const promoCodeApplication = new PromoCodeApplicationService(
      promoCodesService,
      periodBasedPromoCodes,
      paymentPeriod
    );

    this.initApi({
      ...promoCodesService.api(),
      ...validation.api(),
      ...translations.api(),
      ...promoCodeApplication.api(),
      ...periodBasedPromoCodes.api(),
    });

    const isPending =
      planDetailQuery.isPending ||
      customerQuery.isPending ||
      promoCodeValidation.isPending ||
      billingStrategy.isPending ||
      // Stay pending until the rating call has returned, so promo validation (which also hits
      // the rating endpoint for Stripe) is never issued in parallel with the initial rating call.
      ratingQuery.isPending ||
      !ratingRequest;
    const isError = planDetailQuery.isError || customerQuery.isError;

    return {
      status: this.getStatus(isPending, isError),
      data: {
        ...promoCodesService.data(),
        ...visibility.data(),
        ...validation.data(),
        ...translations.data(),
        ...promoCodeApplication.data(),
        ...periodBasedPromoCodes.data(),
      },
      handlers: {
        ...promoCodesService.handlers(),
        ...validation.handlers(),
        ...translations.handlers(),
        ...promoCodeApplication.handlers(),
        ...periodBasedPromoCodes.handlers(),
      },
      lifecycle: {
        onMount() {
          if (urlAdapter) {
            promoCodesService.onMount();
          }
        },
      } as WidgetLifecycle,
    } as const;
  };

  private getStatus(isPending: boolean, isError: boolean) {
    if (isPending) return 'pending';

    if (isError) return 'error';

    return 'loaded';
  }
}
